import type { Action, CardData, PostIt } from '../types';

const RESOLVED_ACTION_STATUSES = new Set(['done', 'completed', 'cancelled']);

export interface MapStrategySimulationImprovements {
  leadTimeReduction: number;
  costReduction: number;
  efficiencyGain: number;
  annualSaving: number;
}

export interface MapStrategyStageSummary {
  cardId: string;
  label: string;
  leadTime: number;
  cumulativeLeadTime: number;
  touchTime: number;
  queueTime: number;
  valueAddedPercent: number;
  wastePercent: number;
  isBottleneck: boolean;
  pendingActions: number;
  hasWasteZone: boolean;
  projectedReduction: number;
  financialImpact: number;
  actionStatusLabel: string;
}

export interface MapStrategyModeMetrics {
  leadTimeTotal: number;
  tpTotal: number;
  queueTimeTotal: number;
  valueAddedPercent: number;
  nonValueAddedPercent: number;
  bottleneckId: string | null;
  bottleneckLabel: string | null;
  stageCount: number;
  reductionTotal: number;
  reductionPercent: number;
  financialImpact: number;
  opportunityCount: number;
  roiEstimated: number;
}

export interface MapStrategyMetrics {
  baseline: MapStrategyModeMetrics;
  kaizen: MapStrategyModeMetrics;
  future: MapStrategyModeMetrics;
  stageSummaries: MapStrategyStageSummary[];
  criticalCardId: string | null;
  pendingActionCardIds: string[];
  wasteCardIds: string[];
  comparisonAvailable: boolean;
}

export interface MapStrategyEngineInput {
  cards: CardData[];
  postIts: PostIt[];
  actions: Action[];
  longestPathIds?: string[] | null;
  bottleneckId?: string | null;
  futureCards?: CardData[] | null;
  simulationImprovements?: Partial<MapStrategySimulationImprovements> | null;
}

function isActivityCard(card: CardData | undefined | null): card is CardData {
  return Boolean(card) && card.tipo === 'atividade' && card.status !== 'removido';
}

function getLeadTime(card: CardData | undefined | null): number {
  if (!card) return 0;
  return Math.max(0, Number(card.lt || card.tp || card.tt || 0));
}

function getProcessingTime(card: CardData | undefined | null): number {
  if (!card) return 0;
  return Math.max(0, Number(card.tt || card.tp || 0));
}

function getCardLabel(card: CardData | undefined | null): string {
  if (!card) return 'Etapa';
  return card.atividade || card.quem || `Etapa ${card.numeroSequencial ?? ''}`.trim() || 'Etapa';
}

function getFlowCardIds(cards: CardData[], explicitIds?: string[] | null): string[] {
  const processCards = cards.filter(isActivityCard);
  if (explicitIds && explicitIds.length > 0) {
    const validIds = explicitIds.filter((id) => processCards.some((card) => card.id === id));
    if (validIds.length > 0) return validIds;
  }

  return [...processCards]
    .sort((a, b) => {
      const seqDiff = (a.numeroSequencial ?? Number.MAX_SAFE_INTEGER) - (b.numeroSequencial ?? Number.MAX_SAFE_INTEGER);
      if (seqDiff !== 0) return seqDiff;
      if (a.posicao.x !== b.posicao.x) return a.posicao.x - b.posicao.x;
      return a.posicao.y - b.posicao.y;
    })
    .map((card) => card.id);
}

function cloneCards(cards: CardData[]): CardData[] {
  return cards.map((card) => ({
    ...card,
    posicao: { ...card.posicao },
    conexoes_saida: (card.conexoes_saida || []).map((conn) => ({
      ...conn,
      waypoints: conn.waypoints ? conn.waypoints.map((point) => ({ ...point })) : undefined,
    })),
    resources: card.resources ? card.resources.map((resource) => ({ ...resource })) : undefined,
    desperdicios: card.desperdicios ? [...card.desperdicios] : undefined,
  }));
}

function getPostItImpacts(postIt: PostIt) {
  if (postIt.impactos && postIt.impactos.length > 0) {
    return postIt.impactos.map((impact) => ({ ...impact }));
  }

  if (postIt.targetNodeId && postIt.improvementType) {
    return [{
      targetNodeId: postIt.targetNodeId,
      tipo: postIt.improvementType === 'tempo_processamento' ? 'TRA' as const : 'TP' as const,
      reducao: Math.max(0, Number(postIt.estimatedGain || 0)),
    }];
  }

  return [];
}

function getActiveOpportunityPostIts(postIts: PostIt[], actions: Action[]): PostIt[] {
  return postIts.filter((postIt) => {
    if (postIt.isMinimized) return false;
    const impacts = getPostItImpacts(postIt);
    if (impacts.length === 0 && !postIt.fixedToNodeId) return false;
    if (!postIt.linkedActionId) return true;

    const linkedAction = actions.find((action) => action.id === postIt.linkedActionId);
    if (!linkedAction) return true;

    return !RESOLVED_ACTION_STATUSES.has(String(linkedAction.status));
  });
}

function applyKaizenImpacts(cards: CardData[], postIts: PostIt[], actions: Action[]) {
  const clonedCards = cloneCards(cards);
  const activeOpportunityPostIts = getActiveOpportunityPostIts(postIts, actions);
  const impactedNodeIds = new Set<string>();

  activeOpportunityPostIts.forEach((postIt) => {
    const impacts = getPostItImpacts(postIt);

    impacts.forEach((impact) => {
      const targetCard = clonedCards.find((card) => card.id === impact.targetNodeId);
      if (!targetCard) return;

      impactedNodeIds.add(targetCard.id);

      const reductionFactor = (100 - Math.max(0, Math.min(100, Number(impact.reducao || 0)))) / 100;

      if (impact.tipo === 'TRA') {
        if (typeof targetCard.tt === 'number') {
          targetCard.tt = targetCard.tt * reductionFactor;
        } else if (targetCard.tt) {
          targetCard.tt = Number(targetCard.tt) * reductionFactor;
        }
        return;
      }

      if (impact.tipo === 'TP') {
        if (typeof targetCard.lt === 'number') {
          targetCard.lt = targetCard.lt * reductionFactor;
        } else if (targetCard.lt) {
          targetCard.lt = Number(targetCard.lt) * reductionFactor;
        } else if (targetCard.tp) {
          targetCard.tp = Number(targetCard.tp) * reductionFactor;
        }
        return;
      }

      targetCard.tt = 0;
      targetCard.lt = 0;
      targetCard.status = 'removido';
    });
  });

  return {
    cards: clonedCards,
    activeOpportunityPostIts,
    impactedNodeIds,
  };
}

function findModeBottleneck(cards: CardData[], flowIds: string[], fallbackId?: string | null) {
  const flowCards = flowIds
    .map((cardId) => cards.find((card) => card.id === cardId))
    .filter(isActivityCard);

  if (flowCards.length === 0) {
    return { id: fallbackId ?? null, label: null as string | null };
  }

  const highestLeadTimeCard = flowCards.reduce((highest, current) =>
    getLeadTime(current) > getLeadTime(highest) ? current : highest
  );

  if (fallbackId && flowCards.some((card) => card.id === fallbackId)) {
    const fallbackCard = flowCards.find((card) => card.id === fallbackId) ?? highestLeadTimeCard;
    return {
      id: fallbackCard.id,
      label: getCardLabel(fallbackCard),
    };
  }

  return {
    id: highestLeadTimeCard.id,
    label: getCardLabel(highestLeadTimeCard),
  };
}

function calculateModeMetrics(
  cards: CardData[],
  flowIds: string[],
  baselineLeadTimeTotal: number,
  options?: {
    fallbackBottleneckId?: string | null;
    financialImpact?: number;
    opportunityCount?: number;
    roiEstimated?: number;
  },
): MapStrategyModeMetrics {
  const flowCards = flowIds
    .map((cardId) => cards.find((card) => card.id === cardId))
    .filter(isActivityCard);

  const leadTimeTotal = flowCards.reduce((sum, card) => sum + getLeadTime(card), 0);
  const tpTotal = flowCards.reduce((sum, card) => sum + getProcessingTime(card), 0);
  const queueTimeTotal = Math.max(0, leadTimeTotal - tpTotal);
  const valueAddedPercent = leadTimeTotal > 0 ? (tpTotal / leadTimeTotal) * 100 : 0;
  const nonValueAddedPercent = Math.max(0, 100 - valueAddedPercent);
  const bottleneck = findModeBottleneck(cards, flowIds, options?.fallbackBottleneckId);
  const reductionTotal = Math.max(0, baselineLeadTimeTotal - leadTimeTotal);
  const reductionPercent = baselineLeadTimeTotal > 0 ? (reductionTotal / baselineLeadTimeTotal) * 100 : 0;

  return {
    leadTimeTotal,
    tpTotal,
    queueTimeTotal,
    valueAddedPercent,
    nonValueAddedPercent,
    bottleneckId: bottleneck.id,
    bottleneckLabel: bottleneck.label,
    stageCount: flowCards.length,
    reductionTotal,
    reductionPercent,
    financialImpact: Math.max(0, Number(options?.financialImpact || 0)),
    opportunityCount: Math.max(0, Number(options?.opportunityCount || 0)),
    roiEstimated: Math.max(0, Number(options?.roiEstimated || 0)),
  };
}

function estimateFinancialImpact(card: CardData, projectedReduction: number, linkedActions: Action[]): number {
  const roiTotal = linkedActions.reduce((sum, action) => sum + Math.max(0, Number(action.roiEstimado || 0)), 0);
  if (roiTotal > 0) return roiTotal;

  const hourlyCost = Math.max(0, Number(card.hourlyCost || 0));
  const demandPerDay = Math.max(1, Number(card.incomingVolume || card.entrada || 1));
  const baselineWaitTime = Math.max(0, getLeadTime(card) - getProcessingTime(card));
  const effectiveReduction = projectedReduction > 0 ? projectedReduction : baselineWaitTime;

  return (effectiveReduction / 60) * hourlyCost * demandPerDay * 22;
}

function buildStageSummaries(
  baselineCards: CardData[],
  projectedCards: CardData[],
  flowIds: string[],
  actions: Action[],
  bottleneckId?: string | null,
): MapStrategyStageSummary[] {
  const projectedCardsById = new Map(projectedCards.map((card) => [card.id, card]));
  let cumulativeLeadTime = 0;

  return flowIds
    .map((cardId) => baselineCards.find((card) => card.id === cardId))
    .filter(isActivityCard)
    .map((card) => {
      const leadTime = getLeadTime(card);
      const touchTime = getProcessingTime(card);
      const queueTime = Math.max(0, leadTime - touchTime);
      const valueAddedPercent = leadTime > 0 ? (touchTime / leadTime) * 100 : 0;
      const wastePercent = Math.max(0, 100 - valueAddedPercent);
      cumulativeLeadTime += leadTime;

      const linkedActions = actions.filter((action) => action.linkedNodeId === card.id);
      const pendingActions = linkedActions.filter((action) => !RESOLVED_ACTION_STATUSES.has(String(action.status))).length;
      const completedActions = Math.max(0, linkedActions.length - pendingActions);
      const projectedCard = projectedCardsById.get(card.id);
      const projectedReduction = Math.max(0, leadTime - getLeadTime(projectedCard));
      const financialImpact = estimateFinancialImpact(card, projectedReduction, linkedActions);

      return {
        cardId: card.id,
        label: getCardLabel(card),
        leadTime,
        cumulativeLeadTime,
        touchTime,
        queueTime,
        valueAddedPercent,
        wastePercent,
        isBottleneck: bottleneckId === card.id,
        pendingActions,
        hasWasteZone: wastePercent >= 45 || (card.desperdicios?.length ?? 0) > 0,
        projectedReduction,
        financialImpact,
        actionStatusLabel:
          linkedActions.length === 0
            ? 'Sem acoes'
            : pendingActions > 0
              ? `${pendingActions} pendente(s)`
              : `${completedActions} concluida(s)`,
      };
    });
}

export function getMetrics(input: MapStrategyEngineInput): MapStrategyMetrics {
  const baselineCards = input.cards.filter(isActivityCard);
  const flowIds = getFlowCardIds(baselineCards, input.longestPathIds);
  const {
    cards: kaizenCards,
    activeOpportunityPostIts,
    impactedNodeIds,
  } = applyKaizenImpacts(baselineCards, input.postIts, input.actions);
  const futureCards = (input.futureCards ?? baselineCards).filter(isActivityCard);

  const baselineBottleneck = findModeBottleneck(baselineCards, flowIds, input.bottleneckId);
  const stageSummaries = buildStageSummaries(
    baselineCards,
    futureCards.length > 0 ? futureCards : kaizenCards,
    flowIds,
    input.actions,
    baselineBottleneck.id,
  );

  const kaizenFinancialImpact = stageSummaries
    .filter((summary) => impactedNodeIds.has(summary.cardId))
    .reduce((sum, summary) => sum + summary.financialImpact, 0);

  const baseline = calculateModeMetrics(baselineCards, flowIds, 0, {
    fallbackBottleneckId: baselineBottleneck.id,
  });
  const kaizen = calculateModeMetrics(kaizenCards, flowIds, baseline.leadTimeTotal, {
    financialImpact: kaizenFinancialImpact,
    opportunityCount: activeOpportunityPostIts.length,
    roiEstimated: kaizenFinancialImpact,
  });
  const future = calculateModeMetrics(futureCards, flowIds, baseline.leadTimeTotal, {
    financialImpact: kaizenFinancialImpact,
    opportunityCount: activeOpportunityPostIts.length,
    roiEstimated: Math.max(0, Number(input.simulationImprovements?.annualSaving || 0)),
  });

  return {
    baseline,
    kaizen,
    future,
    stageSummaries,
    criticalCardId: baseline.bottleneckId,
    pendingActionCardIds: stageSummaries.filter((summary) => summary.pendingActions > 0).map((summary) => summary.cardId),
    wasteCardIds: stageSummaries.filter((summary) => summary.hasWasteZone).map((summary) => summary.cardId),
    comparisonAvailable:
      future.reductionTotal > 0 ||
      kaizen.reductionTotal > 0 ||
      Math.max(0, Number(input.simulationImprovements?.leadTimeReduction || 0)) > 0,
  };
}

export const mapStrategyEngine = {
  getMetrics,
};

export default mapStrategyEngine;
